import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { AvailableAvatarIcons } from '@sparkai/ui'
import { AppTimezonePreference, getTimezonePreferenceQueryKeyPart } from '@sparkai/utils'

import { apiClient } from '../apiClient'
import { getSelectedTimezoneHeaders } from '../timezoneRequestContext'

export interface HourScheduleReport {
  startTime: string
  plannedHours: number
  unplannedHours: number
  unplannedHoursTrend: number | null
  surgeShifts: number
  surgeShiftsTrend: number | null
  timeToFulfillSurge: number
  engagementCount: number
  engagementCountTrend: number | null
  highPriorityPercentage: number
  highPriorityPercentageTrend: number | null
  replacedShifts: number
  replacedShiftsTrend: number | null
  /** Optional until every API deployment returns it; a missing value means no capacity alert. */
  hadCapacityAlert?: boolean
}

type BaseSummary = {
  current: number
  trend: number
}

export interface WeeklyScheduleResponse {
  hours: HourScheduleReport[]
  unplannedHours: BaseSummary
  unclaimedHours: BaseSummary
  replacedShifts: BaseSummary
  resolutionsPerMSPerHour: BaseSummary
  totalHours: BaseSummary
  engagements: BaseSummary
  highPriority: BaseSummary
  avgTimeBusy: BaseSummary
}

type SpecialistReport = {
  id: string
  name: string
  avatarUrl: string | null
}

export interface WeeklySpecialistsResponse {
  totalSpecialists: number
  avgClaimedPerSpecialist: BaseSummary
  specialists: SpecialistReport[]
}

export interface ProgramUtilizationReport {
  color: string
  displayName: string
  highPriorityHours: number
  icon: AvailableAvatarIcons
  lowPriorityHours: number
  programId: string
  programName: string
  share: number
}

export interface WeeklyProgramUtilizationResponse {
  programs: ProgramUtilizationReport[]
}

export interface SupplyDemandResponse {
  totals: {
    highPriorityCount: number
    highPriorityTrend: number
    lowPriorityCount: number
    lowPriorityTrend: number
    highPriorityMissedTargetCount: number
    highPriorityMissedTargetTrend: number
  }
  programs: {
    id: string
    displayName: string
    iconType: string
    color: string
    tasksCount: number
    highPriorityCount: number
    lowPriorityCount: number
    rejectedCount: number
  }[]
  specialists: {
    id: string
    name: string
    avatarUrl: string | null
    plannedHours: number
    unplannedHours: number
    tasks: number
    avgTimeBusy: number
  }[]
  breakdown: {
    startTime: string
    highPriorityWithinTarget: number
    highPriorityMissedTarget: number
    resolvedLowPriority: number
    rejected: number
    plannedHours: number
    unplannedHours: number
    avgTimeBusy: number
    totalEngagements: number
    totalHighPriority: number
    totalLowPriority: number
  }[]
}

type SupplyDemandReportParams = {
  startTime: string
  endTime: string
  breakdownType: 'day' | 'hour' | '5_minutes'
  timezonePreference?: AppTimezonePreference
}

type WeeklyReportParams = {
  weekStartTime: string
  timezonePreference?: AppTimezonePreference
  enabled?: boolean
}

function getWeeklyReportQueryKey(name: string, params: WeeklyReportParams) {
  return params.timezonePreference
    ? [name, params.weekStartTime, getTimezonePreferenceQueryKeyPart(params.timezonePreference)]
    : [name, params.weekStartTime]
}

function getWeeklyReportRequestConfig(params: WeeklyReportParams) {
  const requestConfig = { params: { weekStartTime: params.weekStartTime } }
  if (!params.timezonePreference) return requestConfig

  const headers = getSelectedTimezoneHeaders(params.timezonePreference)
  return Object.keys(headers).length ? { ...requestConfig, headers } : requestConfig
}

export function useGetWeeklyPlanningReport(params: WeeklyReportParams) {
  return useQuery({
    queryKey: getWeeklyReportQueryKey('get-weekly-planning-report', params),
    queryFn: async () => {
      const { data } = await apiClient.get<WeeklyScheduleResponse>(
        '/int/v3/planning/report/weekly',
        getWeeklyReportRequestConfig(params)
      )
      return data
    },
    enabled: params.enabled ?? true,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    gcTime: 60000,
  })
}

export function useGetWeeklySpecialistsReport(params: WeeklyReportParams) {
  return useQuery<WeeklySpecialistsResponse>({
    queryKey: getWeeklyReportQueryKey('get-weekly-specialists-report', params),
    queryFn: async () => {
      const { data } = await apiClient.get<WeeklySpecialistsResponse>(
        '/int/v3/planning/report/weekly/specialists',
        getWeeklyReportRequestConfig(params)
      )
      return data
    },
    enabled: params.enabled ?? true,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    gcTime: 60000,
  })
}

export function useGetWeeklyProgramUtilizationReport(params: WeeklyReportParams) {
  return useQuery({
    queryKey: getWeeklyReportQueryKey('get-weekly-program-utilization-report', params),
    queryFn: async () => {
      const { data } = await apiClient.get<WeeklyProgramUtilizationResponse>(
        '/int/v3/planning/report/weekly/program-utilization',
        getWeeklyReportRequestConfig(params)
      )
      return data
    },
    enabled: params.enabled ?? true,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    gcTime: 60000,
  })
}

function getSupplyDemandReportQueryKey(params: SupplyDemandReportParams) {
  const { timezonePreference, ...requestParams } = params
  return timezonePreference
    ? ['get-supply-demand-report', requestParams, getTimezonePreferenceQueryKeyPart(timezonePreference)]
    : ['get-supply-demand-report', requestParams]
}

function getSupplyDemandReportRequestConfig(params: SupplyDemandReportParams) {
  const { timezonePreference, ...requestParams } = params
  const requestConfig = { params: requestParams }
  if (!timezonePreference) return requestConfig

  const headers = getSelectedTimezoneHeaders(timezonePreference)
  return Object.keys(headers).length ? { ...requestConfig, headers } : requestConfig
}

export function useGetSupplyDemandReport(params: SupplyDemandReportParams) {
  return useQuery({
    queryKey: getSupplyDemandReportQueryKey(params),
    queryFn: async () => {
      const { data } = await apiClient.get<SupplyDemandResponse>(
        '/int/v3/planning/report/supply-demand',
        getSupplyDemandReportRequestConfig(params)
      )
      return data
    },
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    gcTime: 60000,
  })
}
